import React, { ReactNode } from 'react';
import MaterialButton from '@mui/material/Button';
import { SxProps, Theme } from '@mui/system';
import { Box, CircularProgress, CSSObject, SvgIcon } from '@mui/material';

interface ButtonProps {
    label?: string;
    size?: 'large' | 'small';
    variant?: 'primary' | 'outlined' | 'text' | 'success' | 'danger-text' | 'success-text' | 'danger';
    type?: 'button' | 'submit' | 'reset';
    onClick?: () => void;
    fullWidth?: boolean;
    icon?: typeof SvgIcon;
    testId?: string;
    children?: ReactNode;
    sx?: CSSObject;
    disabled?: boolean;
    loading?: boolean;
}

const Button = ({
    label,
    size = 'large',
    variant = 'primary',
    type = 'button',
    onClick,
    fullWidth = false,
    icon: Icon,
    testId,
    sx = {},
    children,
    disabled = false,
    loading = false,
}: ButtonProps) => {
    const getButtonStyles = (): SxProps<Theme> => {
        const baseStyles: SxProps<Theme> = {
            ...sx,
            ...(fullWidth && { width: '100%' }),
            ...(Icon &&
                !label && {
                    minWidth: '24px',
                    minHeight: '24px',
                    width: '24px',
                    height: '24px',
                }),
            ...(size && { height: size === 'large' ? '48px' : '42px' }),
        };

        const variantStyles: Record<string, SxProps<Theme>> = {
            primary: {
                minWidth: '82px',
                backgroundColor: 'common.primary',
                color: 'white',
                '&:hover': {
                    backgroundColor: 'common.primaryHover',
                },
                '&:focus': {
                    outline: 'none',
                },
            },
            outlined: {
                minWidth: '82px',
                borderColor: 'common.primary',
                color: 'common.primary',
                border: '1px solid',
                '&:hover': {
                    borderColor: 'common.primaryHover',
                },
                '&:focus': {
                    outline: 'none',
                },
            },
            text: {
                color: 'common.primary',
                '&:hover': {
                    color: 'common.primaryHover',
                },
                '&:focus': {
                    outline: 'none',
                    boxShadow: 'none',
                },
                height: '100%',
            },
            'danger-text': {
                color: 'common.system.error.main',
                '&:hover': {
                    color: 'common.system.error.main',
                },
                '&:focus': {
                    outline: 'none',
                    boxShadow: 'none',
                },
            },
            'success-text': {
                color: 'common.system.success.main',
                '&:hover': {
                    color: 'common.system.success.main',
                },
                '&:focus': {
                    outline: 'none',
                    boxShadow: 'none',
                },
            },
            danger: {
                minWidth: '82px',
                backgroundColor: 'common.system.error.main',
                color: 'white',
                '&:hover': {
                    backgroundColor: 'common.system.error.main',
                },
                '&:focus': {
                    outline: 'none',
                },
            },
        };

        return {
            ...baseStyles,
            ...variantStyles[variant],
        };
    };

    const buttonStyles = getButtonStyles();

    return (
        <MaterialButton
            variant={variant === 'primary' ? 'contained' : variant.includes('text') ? 'text' : 'outlined'}
            size={size}
            type={type}
            onClick={onClick}
            sx={buttonStyles}
            data-testid={testId ? testId : 'button'}
            startIcon={Icon ? <Icon /> : undefined}
            disableRipple
            disabled={disabled}
            aria-label={label}
        >
            <Box sx={{ visibility: loading ? 'hidden' : 'visible' }}>{label || children}</Box>
            {loading && <CircularProgress data-testid='button-spinner' size={24} sx={{ position: 'absolute' }} />}
        </MaterialButton>
    );
};

export default Button;
